import {
    Component,
    OnInit,
    OnDestroy,
    ViewChild,
    ElementRef,
    Renderer2,
    HostListener,
    ViewContainerRef,
    ComponentRef
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AllModalInformation, ModalContainerOptions } from '../../global/models/Modal.model';
import { toPascalCase } from '../../global/utils.service';
import { CommonModule } from '@angular/common';
import { Router, RouterEvent } from '@angular/router';
import { BgButton, BgButtonComponent, BgIcon, BgIconComponent } from '@Omnissimo/betelgeuse';
import { BootstrapModalService } from '../../global/services/bootstrap-modal/bootstrap-modal.service';
import { MODAL_NAMES_TO_PATHS } from '../../global/services/bootstrap-modal/modal-values.service';
import { BootstrapModalFooterComponent } from './bootstrap-modal-footer.component';

@Component({
    selector: 'bootstrap-modal',
    template: `
    <div class="modal fade" [class.show]="isOpen" [class.d-block]="isOpen" tabindex="-1" role="dialog" #modalDialog>
      <div class="modal-dialog" [class]="containerOptions.exceptionClass" role="document">
        <div class="modal-content">
          <div class="modal-header" *ngIf="containerOptions.buttonDismissable">
            <span class="modal-title" [innerHTML]="modalInfo?.title ?? 'Missing title'"></span>
            <bg-button [shape]="BgButtonShape.CIRCLE" model="secondary" size="large" (click)="closeModal()" aria-label="Close">
                <bg-icon path="./global/ux/icons" icon="close" [shape]="BgIconShape.ICON"
                    [firstDimension]="24" [secondDimension]="24"></bg-icon>
            </bg-button>
            <!-- <button type="button" class="btn-close" (click)="closeModal()" aria-label="Close"></button> -->
          </div>
          <div class="modal-body" [class.modal-body-with-shell-footer]="useShellFooter">
            <div tabindex="0" #firstFocus></div>
            <ng-container #container></ng-container>
            <div tabindex="0" #lastFocus></div>
          </div>
          <bootstrap-modal-footer *ngIf="useShellFooter"></bootstrap-modal-footer>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade" [class.show]="isOpen" *ngIf="isOpen"></div>
  `,
    styleUrls: ['./bootstrap-modal.component.scss'],
    imports: [CommonModule, BgButtonComponent, BgIconComponent, BootstrapModalFooterComponent],
})
export class BootstrapModalComponent implements OnInit, OnDestroy {
    @ViewChild('container', { static: false, read: ViewContainerRef })
    public viewRef!: ViewContainerRef;

    @ViewChild('firstFocus', { read: ElementRef }) set firstFocusRef(el: ElementRef) {
        if (el) {
            this.firstFocus = el.nativeElement;
        }
    }

    @ViewChild('lastFocus', { read: ElementRef }) set lastFocusRef(el: ElementRef) {
        if (el) {
            this.lastFocus = el.nativeElement;
        }
    }

    public containerOptions = new ModalContainerOptions();
    private contentOptions: any;
    public readonly BgButtonShape = BgButton.Shape;
    public readonly BgIconShape = BgIcon.Shape;
    public isOpen = false;
    public useShellFooter = false;
    private activatedComponent: ComponentRef<any> | null = null;
    private readonly onDestroy$ = new Subject<void>();
    private shiftedTabDirection = false;
    public firstFocus: HTMLElement | null = null;
    public lastFocus: HTMLElement | null = null;
    public modalInfo: any;
    // isAnimating = false; // for hiding external elements, like close btn and preventing clicks while animating

    constructor(
        private readonly bootstrapModalService: BootstrapModalService,
        private readonly renderer: Renderer2,
        private readonly router: Router,
        private readonly el: ElementRef,

    ) { }

    ngOnInit() {
        this.bootstrapModalService.openEventBroadcaster
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(
                (modalInfo: AllModalInformation) => {
                    this.modalInfo = modalInfo;
                    const moduleName = modalInfo.moduleName ?? '';
                    const importedModalPromise = MODAL_NAMES_TO_PATHS[moduleName]?.[modalInfo.name];

                    importedModalPromise?.().then(
                        importedFile => {
                            this.renderer.addClass(document.body, 'no-overflow');
                            this.isOpen = true;

                            this.contentOptions = modalInfo.contentOptions;
                            this.containerOptions = this.computeContainerOptions(modalInfo.containerOptions);
                            this.useShellFooter = !!this.containerOptions.useShellFooter;
                            this.bootstrapModalService.clearFooterTemplate();
                            const newComponent = this.createNewComponent(modalInfo, importedFile);

                            if (this.activatedComponent) {
                                //     this.animateExitingEl(this.activatedComponent.location, null, this.containerOptions.reverseAnimation, () => {
                                this.viewRef.remove()
                                //     });
                            }
                            // this.animateEnteringEl(newComponent.location, this.viewRef.length === 1, this.containerOptions.reverseAnimation, () => {
                            this.firstFocus?.focus();
                            //     this.setIsAnimating(false);
                            // });

                            this.activatedComponent = newComponent;
                        }
                    );
                }
            );

        this.bootstrapModalService.closeEventBroadcaster
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(
                value => {
                    this.closeModal(value.route);
                }
            );
    }

    ngOnDestroy() {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    public closeModal(newRoute?: string) {
        if (!this.isOpen) {
            return;
        }

        this.bootstrapModalService.resetOnClose();
        this.viewRef.remove();
        this.activatedComponent = null;
        this.isOpen = false;
        this.useShellFooter = false;

        this.renderer.removeClass(document.body, 'modal-open');
        this.renderer.removeStyle(document.body, 'padding-right');

        // remove only the modal m query param
        this.router.navigate([], { queryParams: { m: null }, queryParamsHandling: 'merge' });

        if (newRoute) {
            this.router.navigate([newRoute])
                .then(() => {
                    this.reloadPageAfterContextSwitch();
                });
        } else {
            this.reloadPageAfterContextSwitch();
        }
    }

    private reloadPageAfterContextSwitch(): void {
        // if (this.contextService.getReloadAfterContextSwitchFlag()) {
        if (false) {
            window.location.reload();
        }
        // }
    }

    private createNewComponent(modalInfo: any, importedFile: any) {
        const componentName = `${toPascalCase(modalInfo.name)}Component`;
        return this.viewRef.createComponent(importedFile[componentName], { index: 0 });

    }

    private computeContainerOptions(options: ModalContainerOptions) {
        if (options && Object.keys(options).length) {
            if (options.isFlow) {
                return {
                    ...this.containerOptions,
                    isFlow: options.isFlow,
                    // reverseAnimation: options.reverseAnimation || this.containerOptions.reverseAnimation
                }
            }
            else {
                return { ...new ModalContainerOptions(), ...options }
            }
        } else {
            return new ModalContainerOptions();
        }

    }

    // before browser back
    @HostListener('window:popstate', ['$event'])
    onPopState(event: RouterEvent) {
        if (this.isOpen) {
            this.closeModal();
        }
    }

    @HostListener('document:keydown.escape', ['$event'])
    onKeydownHandler(event: KeyboardEvent) {
        if (this.containerOptions.backdropDismissable || this.containerOptions.buttonDismissable) {
            this.closeModal();
        }
    }

    // Backdrop click event
    @HostListener('mousedown', ['$event'])
    onMouseDownHandler(event: MouseEvent) {
        if (event.button === 0 && event.target === this.el.nativeElement && this.containerOptions.backdropDismissable) {
            this.closeModal();
        }
    }

    @HostListener('keydown.tab', ['$event'])
    onTabPress(event: KeyboardEvent) {
        this.shiftedTabDirection = false;
        if (event.target === this.lastFocus) {
            event.preventDefault();
            this.firstFocus?.focus();
        }
    }

    @HostListener('keydown.shift.tab', ['$event'])
    onShiftTabPress(event: KeyboardEvent) {
        this.shiftedTabDirection = true;
        if (event.target === this.firstFocus) {
            event.preventDefault();
            this.lastFocus?.focus();
        }
    }

    @HostListener('focusin', ['$event'])
    onFocusIn(event: FocusEvent) {
        if (!this.shiftedTabDirection && event.target === this.lastFocus) {
            this.firstFocus?.focus();
        }
    }
}
